import { useEffect, useState, useCallback, useRef } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, where, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../utils/init-firebase';
import { Eye, Edit, Download, Trash, X, Upload, FileText } from 'lucide-react';
import '../index.css';

const Search = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchFields, setSearchFields] = useState({});
  const [pendingSearchFields, setPendingSearchFields] = useState({});
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editData, setEditData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [suggestionsData, setSuggestionsData] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingPDFs, setUploadingPDFs] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [deletingPDFs, setDeletingPDFs] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Cache for suggestions data
  const suggestionsCache = useRef({});

  const desiredColumns = [
    'Date', 'Model', 'MTM', 'Serial Number',
    'Purchased From', 'Bill To', 'Invoice', 'Client PO Number'
  ];

  const isHiddenField = (col) =>
    col.toLowerCase().includes('pdf') || col.toLowerCase() === 'id';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const snapshot = await getDocs(collection(db, 'cloudtree-database'));
        const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setData(docs);
        setFilteredData(docs);
        setTotalPages(Math.ceil(docs.length / rowsPerPage));

        // Generate suggestions only for visible columns
        const newSuggestions = {};
        desiredColumns.forEach(col => {
          if (!suggestionsCache.current[col]) {
            const valuesSet = new Set();
            docs.forEach(doc => {
              if (doc[col]) {
                valuesSet.add(doc[col]);
              }
            });
            suggestionsCache.current[col] = Array.from(valuesSet).sort((a, b) => {
              const valA = String(a).toLowerCase();
              const valB = String(b).toLowerCase();
              if (valA < valB) return -1;
              if (valA > valB) return 1;
              return 0;
            });
          }
          newSuggestions[col] = suggestionsCache.current[col];
        });
        setSuggestionsData(newSuggestions);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Update filtered data when search fields change
  useEffect(() => {
    const filtered = data.filter((item) =>
      desiredColumns.every((field) => {
        const val = searchFields[field];
        return !val || item[field]?.toLowerCase().includes(val.toLowerCase());
      })
    );
    setFilteredData(filtered);
    setTotalPages(Math.ceil(filtered.length / rowsPerPage));
    setCurrentPage(1);
  }, [searchFields, data]);

  // Get current page data
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  const filteredSuggestions = useCallback((field) => {
    if (!pendingSearchFields[field]) {
      return suggestionsData[field] || [];
    }
    const inputVal = pendingSearchFields[field].toLowerCase();
    const filtered = (suggestionsData[field] || []).filter(s =>
      String(s).toLowerCase().includes(inputVal)
    );
    return filtered.sort((a, b) => String(a).localeCompare(String(b))).slice(0, 5);
  }, [pendingSearchFields, suggestionsData]);

  const handleSuggestionClick = (field, suggestion) => {
    setPendingSearchFields(prev => ({
      ...prev,
      [field]: suggestion,
    }));
    setShowSuggestions(prev => ({ ...prev, [field]: false }));
    setActiveSuggestionIndex(prev => ({ ...prev, [field]: -1 }));
  };

  const handleBlur = (field) => {
    setTimeout(() => {
      setShowSuggestions(prev => ({ ...prev, [field]: false }));
      setActiveSuggestionIndex(prev => ({ ...prev, [field]: -1 }));
    }, 150);
  };

  const handleKeyDown = (e, field) => {
    const suggestions = filteredSuggestions(field);
    const currentIndex = activeSuggestionIndex[field] ?? -1;

    if (showSuggestions[field] && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = currentIndex + 1 >= suggestions.length ? 0 : currentIndex + 1;
        setActiveSuggestionIndex(prev => ({ ...prev, [field]: nextIndex }));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = currentIndex - 1 < 0 ? suggestions.length - 1 : currentIndex - 1;
        setActiveSuggestionIndex(prev => ({ ...prev, [field]: prevIndex }));
      } else if (e.key === 'Enter') {
        if (currentIndex >= 0 && currentIndex < suggestions.length) {
          e.preventDefault();
          handleSuggestionClick(field, suggestions[currentIndex]);
        }
      }
    }
  };

  const handleSearchChange = (col, value) => {
    const newFields = { ...searchFields, [col]: value };
    setSearchFields(newFields);
    setShowSuggestions(prev => ({ ...prev, [col]: true }));
    setActiveSuggestionIndex(prev => ({ ...prev, [col]: -1 }));

    const filtered = data.filter((item) =>
      desiredColumns.every((field) => {
        const val = newFields[field];
        return !val || item[field]?.toLowerCase().includes(val.toLowerCase());
      })
    );
    setFilteredData(filtered);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setEditData({});
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setEditData(item);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    const confirmDelete = window.confirm(`Delete "${item.Model || 'this item'}"?`);
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, 'cloudtree-database', item.id));
      setData((prev) => prev.filter((d) => d.id !== item.id));
      setFilteredData((prev) => prev.filter((d) => d.id !== item.id));
    } catch (err) {
      console.error(err);
      alert("Error deleting item");
    }
  };

  const handleSave = async () => {
    if (!selectedItem || !selectedItem.id) return;

    try {
      const docRef = doc(db, 'cloudtree-database', selectedItem.id);
      const updatedData = { ...editData };
      delete updatedData.id;

      Object.keys(updatedData).forEach((key) => {
        if (isHiddenField(key)) delete updatedData[key];
      });

      await updateDoc(docRef, updatedData);
      setData((prev) =>
        prev.map((item) =>
          item.id === selectedItem.id ? { ...item, ...updatedData } : item
        )
      );
      setFilteredData((prev) =>
        prev.map((item) =>
          item.id === selectedItem.id ? { ...item, ...updatedData } : item
        )
      );
      closeModal();
    } catch (err) {
      console.error(err);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setEditData({});
    setIsEditing(false);
  };

  // Update allColumns whenever data changes (all columns except id/pdf)
  useEffect(() => {
    if (data.length > 0) {
      const columnsSet = new Set();
      data.forEach(item => {
        Object.keys(item).forEach(key => {
          if (!isHiddenField(key)) columnsSet.add(key);
        });
      });
      const columnsArr = Array.from(columnsSet).sort((a, b) => a.localeCompare(b));
      setAllColumns(columnsArr);
      setPendingSearchFields(fields => {
        // Ensure all fields exist in pendingSearchFields
        const newFields = { ...fields };
        columnsArr.forEach(col => { if (!(col in newFields)) newFields[col] = ''; });
        return newFields;
      });
    } else {
      setAllColumns([]);
    }
  }, [data]);

  // Suggestions for all search fields
  useEffect(() => {
    if (data.length > 0) {
      const newSuggestions = {};
      allColumns.forEach(col => {
        if (!suggestionsCache.current[col]) {
          const valuesSet = new Set();
          data.forEach(doc => {
            if (doc[col]) valuesSet.add(doc[col]);
          });
          suggestionsCache.current[col] = Array.from(valuesSet).sort((a, b) => String(a).localeCompare(String(b)));
        }
        newSuggestions[col] = suggestionsCache.current[col];
      });
      setSuggestionsData(newSuggestions);
    }
  }, [data, allColumns]);

  // New handleSearch: build Firestore query from filled fields
  const handleSearch = async () => {
    // Collect all non-empty search fields
    const filledFields = Object.entries(pendingSearchFields)
      .filter(([key, value]) => value && value.trim() !== '');
    if (filledFields.length === 0) {
      setFilteredData([]);
      setSearchTriggered(true);
      setTotalPages(1);
      setCurrentPage(1);
      return;
    }
    setIsSearching(true);
    try {
      let q = collection(db, 'cloudtree-database');
      // Build .where() for each field
      filledFields.forEach(([key, value]) => {
        q = query(q, where(key, '==', value));
      });
      // Optionally, add a limit (remove if you want all results)
      // q = query(q, limit(100));
      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFilteredData(docs);
      setTotalPages(Math.max(1, Math.ceil(docs.length / rowsPerPage)));
      setCurrentPage(1);
      setSearchTriggered(true);
    } catch (error) {
      console.error('Error searching:', error);
      setFilteredData([]);
      setTotalPages(1);
      setCurrentPage(1);
      setSearchTriggered(true);
    } finally {
      setIsSearching(false);
    }
  };

  // Update pendingSearchFields as user types
  const handlePendingSearchChange = (col, value) => {
    setPendingSearchFields(prev => ({ ...prev, [col]: value }));
    setShowSuggestions(prev => ({ ...prev, [col]: true }));
    setActiveSuggestionIndex(prev => ({ ...prev, [col]: -1 }));
  };

  // CSV download function with selected columns
  const downloadCSVWithColumns = (columnsToInclude) => {
    if (!filteredData.length) {
      alert("No data to download.");
      return;
    }
    if (!columnsToInclude.length) {
      alert("Please select at least one column.");
      return;
    }
    const header = columnsToInclude.join(',');
    const rows = filteredData.map(item =>
      columnsToInclude
        .map(col => {
          let cell = item[col];
          if (cell === undefined || cell === null) cell = '';
          else cell = cell.toString().replace(/"/g, '""');
          return /[",\n]/.test(cell) ? `"${cell}"` : cell;
        })
        .join(',')
    );
    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `search_results_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Modal handlers
  const handleOpenCSVModal = () => setShowCSVModal(true);
  const handleCloseCSVModal = () => setShowCSVModal(false);
  const handleColumnCheckbox = (col) => {
    setSelectedColumns(prev =>
      prev.includes(col)
        ? prev.filter(c => c !== col)
        : [...prev, col]
    );
  };
  const handleSelectAllColumns = () => setSelectedColumns(allColumns);
  const handleDeselectAllColumns = () => setSelectedColumns([]);
  const handleSubmitCSVModal = (e) => {
    e.preventDefault();
    downloadCSVWithColumns(selectedColumns);
    setShowCSVModal(false);
  };

  // PDF Upload Functions
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate files
    const invalidFiles = files.filter(file => !file.type.includes('pdf'));
    if (invalidFiles.length > 0) {
      setUploadError('Only PDF files are allowed');
      return;
    }

    uploadPDFs(files);
  };

  const uploadPDFs = async (files) => {
    if (!selectedItem || !selectedItem.id) return;

    setUploadingPDFs(true);
    setUploadProgress(0);
    setUploadError('');

    try {
      const uploadPromises = files.map(async (file, index) => {
        // Create a unique filename
        const timestamp = new Date().getTime();
        const filename = `${selectedItem.id}_${timestamp}_${file.name}`;
        const storageRef = ref(storage, `pdfs/${filename}`);

        // Upload file
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);

        return {
          name: file.name,
          url: downloadURL,
          path: storageRef.fullPath
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      // Update Firestore document with new PDF URLs
      const docRef = doc(db, 'cloudtree-database', selectedItem.id);
      const currentPDFS = selectedItem.PDFS || [];
      const updatedPDFS = [...currentPDFS, ...uploadedFiles];

      await updateDoc(docRef, { PDFS: updatedPDFS });

      // Update local state
      const updatedItem = {
        ...selectedItem,
        PDFS: updatedPDFS
      };
      setSelectedItem(updatedItem);
      setEditData(updatedItem);

      // Update data and filteredData
      setData(prev =>
        prev.map(item =>
          item.id === selectedItem.id ? updatedItem : item
        )
      );
      setFilteredData(prev =>
        prev.map(item =>
          item.id === selectedItem.id ? updatedItem : item
        )
      );

    } catch (error) {
      console.error('Error uploading PDFs:', error);
      setUploadError('Failed to upload PDFs. Please try again.');
    } finally {
      setUploadingPDFs(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePDF = async (pdfToDelete) => {
    if (!selectedItem || !selectedItem.id) return;
    setDeletingPDFs(prev => [...prev, pdfToDelete.path]);
    try {
      // Delete from Storage
      const storageRef = ref(storage, pdfToDelete.path);
      await deleteObject(storageRef);

      // Update Firestore
      const docRef = doc(db, 'cloudtree-database', selectedItem.id);
      const updatedPDFS = (selectedItem.PDFS || []).filter(pdf => pdf.path !== pdfToDelete.path);
      await updateDoc(docRef, { PDFS: updatedPDFS });

      // Update local state
      const updatedItem = {
        ...selectedItem,
        PDFS: updatedPDFS
      };
      setSelectedItem(updatedItem);
      setEditData(updatedItem);
      setData(prev =>
        prev.map(item =>
          item.id === selectedItem.id ? updatedItem : item
        )
      );
      setFilteredData(prev =>
        prev.map(item =>
          item.id === selectedItem.id ? updatedItem : item
        )
      );
    } catch (error) {
      console.error('Error deleting PDF:', error);
      alert('Failed to delete PDF. Please try again. ' + (error?.message || ''));
    } finally {
      setDeletingPDFs(prev => prev.filter(path => path !== pdfToDelete.path));
    }
  };

  const handleDownloadPDF = (pdf) => {
    if (pdf && pdf.url) {
      window.open(pdf.url, '_blank');
    }
  };

  // Modify the modal content to include PDF section
  const renderPDFSection = () => {
    if (!selectedItem) return null;

    const pdfs = selectedItem.PDFS || [];

    return (
      <div className="modal-pdf-section">
        <div className="modal-pdf-header">
          <h3 className="modal-pdf-title">Attached PDFs</h3>
          <label className="modal-pdf-upload">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".pdf"
              multiple
              className="pdf-upload-input"
              disabled={uploadingPDFs}
            />
            <Upload size={16} />
            Upload PDFs
          </label>
        </div>

        {uploadingPDFs && (
          <div className="pdf-upload-progress">
            <div
              className="pdf-upload-progress-bar"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        )}

        {uploadError && (
          <div className="pdf-upload-error">{uploadError}</div>
        )}

        {pdfs.length > 0 ? (
          <div className="pdf-list">
            {pdfs.map((pdf, index) => (
              <div key={index} className="pdf-item">
                <div className="pdf-item-info" onClick={() => handleDownloadPDF(pdf)} style={{ cursor: 'pointer' }}>
                  <FileText size={20} className="pdf-item-icon" />
                  <span className="pdf-item-name">{pdf.name}</span>
                </div>
                <div className="pdf-item-actions">
                  <button
                    className="pdf-item-button delete"
                    onClick={() => handleDeletePDF(pdf)}
                    title="Delete PDF"
                    disabled={deletingPDFs.includes(pdf.path)}
                  >
                    {deletingPDFs.includes(pdf.path) ? (
                      <span className="spinner" style={{ width: 18, height: 18, display: 'inline-block', verticalAlign: 'middle' }}></span>
                    ) : (
                      <Trash size={18} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="pdf-upload-container">
            <label className="pdf-upload-label">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf"
                multiple
                className="pdf-upload-input"
                disabled={uploadingPDFs}
              />
              <Upload size={24} className="pdf-upload-icon" />
              <div className="pdf-upload-text">
                <strong>Click to upload</strong>
              </div>
            </label>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="secret-pages">
      <div className="dashboard-wrapper">
        <div className="dashboard-container">
          <h1 className="dashboard-header">Search Database</h1>
          {isLoading ? (
            <p className="loading-message">Loading data...</p>
          ) : (
            <>
              <div className="search-inputs-grid">
                {allColumns.map((col) => (
                  <div key={col} className="search-input-wrapper">
                    <label>{col}</label>
                    <div className="search-input-container">
                      <input
                        type="text"
                        value={pendingSearchFields[col] || ''}
                        onChange={(e) => handlePendingSearchChange(col, e.target.value)}
                        onBlur={() => handleBlur(col)}
                        onKeyDown={(e) => handleKeyDown(e, col)}
                        placeholder={`Search ${col}`}
                        className="search-input"
                      />
                      {showSuggestions[col] && filteredSuggestions(col).length > 0 && (
                        <ul className="suggestions-list">
                          {filteredSuggestions(col).map((sugg, idx) => (
                            <li
                              key={idx}
                              onMouseDown={() => handleSuggestionClick(col, sugg)}
                              className={idx === activeSuggestionIndex[col] ? 'active-suggestion' : ''}
                            >
                              {sugg}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ margin: '1rem 0' }}>
                <button className="submit-btn" onClick={handleSearch} disabled={isSearching}>Search</button>
              </div>
              <div className="row-limit-control">
                <label htmlFor="rowsPerPage">Rows per page:</label>
                <select
                  id="rowsPerPage"
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                >
                  {[10, 25, 50, 100].map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
                <button
                  className="download-csv-btn"
                  onClick={handleOpenCSVModal}
                  type="button"
                >
                  Download CSV
                </button>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    {desiredColumns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {searchTriggered && getCurrentPageData().length > 0 ? (
                    getCurrentPageData().map((item) => (
                      <tr key={item.id}>
                        {desiredColumns.map((col) => (
                          <td key={col}>{item[col]}</td>
                        ))}
                        <td>
                          <div className="action-icons">
                            <Eye size={20} onClick={() => handleView(item)} />
                            <Edit size={20} onClick={() => handleEdit(item)} />
                            <Trash size={20} onClick={() => handleDelete(item)} />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      {desiredColumns.map((col) => (
                        <td key={col}></td>
                      ))}
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination controls */}
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showModal && selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditing ? 'Edit Details' : 'View Details'}</h2>
              <button className="modal-close" onClick={closeModal} aria-label="Close modal">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-grid">
                {Object.keys(selectedItem)
                  .filter((col) => !isHiddenField(col))
                  .sort((a, b) => a.localeCompare(b))
                  .map((col) => (
                    <div key={col} className="modal-field">
                      <label className="modal-label">{col}</label>
                      {isEditing ? (
                        <input
                          type="text"
                          className="modal-input"
                          value={editData[col] || ''}
                          onChange={(e) =>
                            setEditData({ ...editData, [col]: e.target.value })
                          }
                          placeholder={`Enter ${col}`}
                        />
                      ) : (
                        <div className="modal-value">{selectedItem[col] || 'N/A'}</div>
                      )}
                    </div>
                  ))}
              </div>
              {renderPDFSection()}
            </div>
            {isEditing && (
              <div className="modal-footer">
                <button className="modal-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button className="modal-save" onClick={handleSave}>
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSV Column Selection Modal */}
      {showCSVModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>Select Columns to Download</h2>
              <button className="modal-close" onClick={handleCloseCSVModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmitCSVModal}>
              <div className="modal-body" style={{ maxHeight: 300, overflowY: 'auto' }}>
                <div className="csv-modal-btn-row">
                  <button type="button" className="csv-modal-btn csv-modal-btn-select" onClick={handleSelectAllColumns}>Select All</button>
                  <button type="button" className="csv-modal-btn csv-modal-btn-deselect" onClick={handleDeselectAllColumns}>Deselect All</button>
                </div>
                {allColumns.length === 0 && <p>No columns available.</p>}
                {allColumns.map(col => (
                  <div key={col} style={{ marginBottom: 6 }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(col)}
                        onChange={() => handleColumnCheckbox(col)}
                      />
                      {col}
                    </label>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="button" className="modal-cancel" onClick={handleCloseCSVModal}>Cancel</button>
                <button type="submit" className="modal-save" style={{ marginLeft: 8 }}>Download</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;
